import Dexie, { type Table } from 'dexie';
import type { 
  PlayerProfile, 
  GameRecord, 
  LeaderboardEntry, 
  Achievement,
  DailyChallenge 
} from '@/types';

export type { LeaderboardEntry, GameRecord };

export class PokerDB extends Dexie {
  profiles!: Table<PlayerProfile>;
  gameRecords!: Table<GameRecord>;
  leaderboard!: Table<LeaderboardEntry>;
  achievements!: Table<Achievement>;
  dailyChallenges!: Table<DailyChallenge>;
  settings!: Table<{ profileId: string; key: string; value: unknown }>;

  constructor() {
    super('TexasHoldemDB');
    
    this.version(1).stores({
      profiles: 'id, name, score, chips',
      gameRecords: 'id, profileId, timestamp, mode, [profileId+timestamp]',
      leaderboard: 'id, profileId, weekKey, score, rank',
      achievements: 'id',
      dailyChallenges: 'id, date',
      settings: '[profileId+key]',
    });
  }
}

export const db = new PokerDB();

// 玩家档案操作
export async function createProfile(name: string, avatar: string): Promise<PlayerProfile> {
  const now = Date.now();
  const today = new Date().toISOString().split('T')[0];
  
  const profile: PlayerProfile = {
    id: `profile-${now}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    avatar,
    chips: 10000,
    score: 0,
    createdAt: now,
    lastLoginDate: today,
    dailyReliefCount: 0,
    stats: {
      totalGames: 0,
      wins: 0,
      winRate: 0,
      biggestPot: 0,
      currentStreak: 0,
      bestStreak: 0,
      royalFlushCount: 0,
      bluffSuccessCount: 0,
      totalHandsPlayed: 0,
      vpip: 0,
      pfr: 0,
    },
  };
  
  await db.profiles.add(profile);
  return profile;
}

export async function getProfile(id: string): Promise<PlayerProfile | undefined> {
  return db.profiles.get(id);
}

export async function getAllProfiles(): Promise<PlayerProfile[]> {
  return db.profiles.toArray();
}

export async function updateProfile(
  id: string, 
  updates: Partial<PlayerProfile>
): Promise<void> {
  await db.profiles.update(id, updates);
}

export async function deleteProfile(id: string): Promise<void> {
  await db.profiles.delete(id);
  // 同时删除相关记录
  await db.gameRecords.where('profileId').equals(id).delete();
  await db.leaderboard.where('profileId').equals(id).delete();
}

// 每日签到
export async function checkIn(profileId: string): Promise<{ success: boolean; reward: number }> {
  const profile = await getProfile(profileId);
  if (!profile) return { success: false, reward: 0 };
  
  const today = new Date().toISOString().split('T')[0];
  
  if (profile.lastLoginDate === today) {
    return { success: false, reward: 0 }; // 今天已签到
  }
  
  const reward = 500;
  await updateProfile(profileId, {
    chips: profile.chips + reward,
    lastLoginDate: today,
    dailyReliefCount: 0, // 重置救济金次数
  });
  
  return { success: true, reward };
}

// 破产救济
export async function claimRelief(profileId: string): Promise<{ success: boolean; amount: number; remaining: number }> {
  const profile = await getProfile(profileId);
  if (!profile) return { success: false, amount: 0, remaining: 0 };
  
  const today = new Date().toISOString().split('T')[0];
  
  // 检查是否今天第一次领取
  if (profile.lastLoginDate !== today) {
    await updateProfile(profileId, {
      lastLoginDate: today,
      dailyReliefCount: 0,
    });
    profile.dailyReliefCount = 0;
  }
  
  if (profile.chips >= 100) {
    return { success: false, amount: 0, remaining: 3 - profile.dailyReliefCount };
  }
  
  if (profile.dailyReliefCount >= 3) {
    return { success: false, amount: 0, remaining: 0 };
  }
  
  const reliefAmount = 2000;
  await updateProfile(profileId, {
    chips: profile.chips + reliefAmount,
    dailyReliefCount: profile.dailyReliefCount + 1,
  });
  
  return { 
    success: true, 
    amount: reliefAmount, 
    remaining: 2 - profile.dailyReliefCount 
  };
}

// 游戏记录操作
export async function saveGameRecord(record: GameRecord): Promise<void> {
  await db.gameRecords.add(record);
  
  // 更新玩家统计
  const profile = await getProfile(record.profileId);
  if (profile) {
    const isWin = record.placement === 1;
    const newStreak = isWin ? profile.stats.currentStreak + 1 : 0;
    
    await updateProfile(record.profileId, {
      score: profile.score + record.scoreChange,
      chips: profile.chips + record.chipChange,
      stats: {
        ...profile.stats,
        totalGames: profile.stats.totalGames + 1,
        wins: profile.stats.wins + (isWin ? 1 : 0),
        winRate: ((profile.stats.wins + (isWin ? 1 : 0)) / (profile.stats.totalGames + 1)) * 100,
        currentStreak: newStreak,
        bestStreak: Math.max(profile.stats.bestStreak, newStreak),
        biggestPot: Math.max(profile.stats.biggestPot, Math.abs(record.chipChange)),
      },
    });
    
    // 更新排行榜
    await updateLeaderboard(record.profileId, profile.name, profile.avatar, record.scoreChange);
  }
}

export async function getPlayerRecords(profileId: string): Promise<GameRecord[]> {
  return db.gameRecords
    .where('profileId')
    .equals(profileId)
    .reverse()
    .sortBy('timestamp');
}

export async function getRecentRecords(profileId: string, limit: number = 10): Promise<GameRecord[]> {
  return db.gameRecords
    .where('profileId')
    .equals(profileId)
    .reverse()
    .limit(limit)
    .sortBy('timestamp');
}

// 排行榜操作
export async function updateLeaderboard(
  profileId: string, 
  name: string, 
  avatar: string, 
  scoreChange: number
): Promise<void> {
  const weekKey = getWeekKey();
  const id = `${profileId}-${weekKey}`;
  
  const existing = await db.leaderboard.get(id);
  
  if (existing) {
    await db.leaderboard.update(id, {
      score: existing.score + scoreChange,
    });
  } else {
    await db.leaderboard.add({
      id,
      profileId,
      name,
      avatar,
      score: scoreChange,
      weekKey,
      rank: 0,
    });
  }
  
  // 重新计算排名
  await recalculateRanks(weekKey);
}

export async function getLeaderboard(weekKey?: string, _limit: number = 50): Promise<LeaderboardEntry[]> {
  const key = weekKey || getWeekKey();
  return db.leaderboard
    .where('weekKey')
    .equals(key)
    .reverse()
    .sortBy('score');
}

export async function getPlayerRank(profileId: string, weekKey?: string): Promise<number> {
  const key = weekKey || getWeekKey();
  const entry = await db.leaderboard.get(`${profileId}-${key}`);
  return entry?.rank || 0;
}

async function recalculateRanks(weekKey: string): Promise<void> {
  const entries = await db.leaderboard
    .where('weekKey')
    .equals(weekKey)
    .reverse()
    .sortBy('score');
  
  for (let i = 0; i < entries.length; i++) {
    await db.leaderboard.update(entries[i].id, { rank: i + 1 });
  }
}

function getWeekKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const week = getWeekNumber(now);
  return `${year}-W${week}`;
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// 设置操作
export async function getSetting<T>(profileId: string, key: string, defaultValue: T): Promise<T> {
  const setting = await db.settings.get([profileId, key]);
  return (setting?.value as T) ?? defaultValue;
}

export async function setSetting(profileId: string, key: string, value: unknown): Promise<void> {
  await db.settings.put({ profileId, key, value });
}

// 导出/导入数据
export async function exportPlayerData(profileId: string): Promise<string> {
  const profile = await getProfile(profileId);
  const records = await getPlayerRecords(profileId);
  
  const data = {
    profile,
    records,
    exportedAt: Date.now(),
  };
  
  return JSON.stringify(data);
}

export async function importPlayerData(jsonData: string): Promise<PlayerProfile | null> {
  try {
    const data = JSON.parse(jsonData);
    
    if (data.profile) {
      // 生成新的ID避免冲突
      const newId = `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const newProfile = {
        ...data.profile,
        id: newId,
        name: `${data.profile.name} (导入)`,
        createdAt: Date.now(),
      };
      
      await db.profiles.add(newProfile);
      
      // 导入游戏记录
      if (data.records && Array.isArray(data.records)) {
        for (const record of data.records) {
          await db.gameRecords.add({
            ...record,
            id: `record-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            profileId: newId,
          });
        }
      }
      
      return newProfile;
    }
    
    return null;
  } catch (error) {
    console.error('导入数据失败:', error);
    return null;
  }
}
